import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SA_COLORS = {
  'AI Workforce': { bg: '#dbeafe', color: '#1d4ed8' },
  'Security':     { bg: '#e0f2fe', color: '#0369a1' },
};
function SABadge({ name }) {
  const cfg = SA_COLORS[name] || { bg: '#f1f5f9', color: '#475569' };
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>
      {name || '—'}
    </span>
  );
}

function PaginationBar({ page, totalPages, total, perPage, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  return (
    <div className="pagination" data-testid="compete-hub-downloads-pagination">
      <span className="pagination-info">{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

export default function InsightsCubeCompeteHub() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  const fetchData = useCallback(async (p, from, to) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const params = { page: p, per_page: 100 };
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const r = await axios.get(`${API}/api/lup/v2/feeds/compete-hub-downloads`, { params, withCredentials: true });
      if (r.data?.success === false) {
        setErrorMsg(r.data.message || 'Compete Hub raw download data is temporarily unavailable. Please try again shortly.');
        setData([]);
        setTotal(0);
        setTotalPages(1);
      } else {
        setData(r.data.data || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.last_page || 1);
        setPage(r.data.current_page || p);
      }
    } catch (e) {
      setErrorMsg('Compete Hub raw download data is temporarily unavailable. Please try again shortly.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(1, '', ''); }, []); // eslint-disable-line

  const handleApply = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    fetchData(1, dateFrom, dateTo);
  };

  const handleReset = () => {
    setDateFrom(''); setDateTo('');
    setAppliedFrom(''); setAppliedTo('');
    fetchData(1, '', '');
  };

  return (
    <div data-testid="compete-hub-downloads-page">
      <div className="page-header">
        <h1>Insights Cube — Compete Hub</h1>
        <p>Raw, row-level downloads feed mirroring exactly what is sent to Microsoft's MAQ Insights Cube team.</p>
      </div>

      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group">
          <label className="filter-label">Downloaded from</label>
          <input type="date" className="filter-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="compete-hub-downloads-date-from" />
        </div>
        <div className="filter-group">
          <label className="filter-label">Downloaded to</label>
          <input type="date" className="filter-input" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="compete-hub-downloads-date-to" />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={handleApply} data-testid="compete-hub-downloads-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={handleReset} data-testid="compete-hub-downloads-reset-btn">Reset</button>
        </div>
      </div>

      <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 16px' }}>
        This is the exact data feed sent to Microsoft MAQ Insights Cube.
      </p>

      <div className="table-container">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : errorMsg ? (
          <div data-testid="compete-hub-downloads-error" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '24px 20px' }}>
            <AlertCircle size={18} className="text-amber-500" color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>{errorMsg}</p>
          </div>
        ) : data.length === 0 ? (
          <div className="empty-state">
            <h3>No downloads found</h3>
            <p>{appliedFrom || appliedTo ? 'Try a different date range.' : 'No download events recorded yet.'}</p>
          </div>
        ) : (
          <>
            <div className="table-header">
              <div>
                <span className="table-title">Compete Hub Raw Downloads</span>
                <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} row{total !== 1 ? 's' : ''})</span>
              </div>
            </div>
            <div className="table-scroll">
              <table data-testid="compete-hub-downloads-table">
                <thead>
                  <tr>
                    <th>Downloaded At</th>
                    <th>Asset Title</th>
                    <th>Solution Area</th>
                    <th>Content Type</th>
                    <th>Language</th>
                    <th>LMS ID</th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Organization</th>
                    <th>Country</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map(row => (
                    <tr key={row.download_id} data-testid={`compete-hub-download-row-${row.download_id}`}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#64748b' }}>{row.downloaded_at || '—'}</td>
                      <td style={{ maxWidth: 260, fontSize: 13 }}>{row.asset_title || '—'}</td>
                      <td><SABadge name={row.solution_area} /></td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>{row.content_type || '—'}</td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>{row.language || '—'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#475569' }}>{row.lms_id || '—'}</td>
                      <td style={{ fontSize: 13 }}>{row.user_name || '—'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#3182ce' }}>{row.user_email || '—'}</td>
                      <td style={{ fontSize: 12 }}>{row.organization || '—'}</td>
                      <td style={{ fontSize: 12 }}>{row.country || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} total={total} perPage={100}
              onPage={p => fetchData(p, appliedFrom, appliedTo)} />
          </>
        )}
      </div>
    </div>
  );
}
